import { CheckOutlined, DownOutlined } from '../../components/icons'
import { DropdownMenu } from '../../components/ui/DropdownMenu'
import type { RegionTierId, RegionTierOption } from './custom-region-tiers'

export interface CustomLeadTimeControlProps {
  /**
   * Code-review Fix 3 — pre-derived by the caller (`ShipmentBreakdown`, via
   * `deriveRegionTierOptions`) rather than re-derived in here from
   * `baseDate`/`customSubtotalAmount`/`currency`/`selectedTierId`: the
   * caller ALSO needs these options itself (for the custom card's sourcing-
   * region note), so deriving twice per render was redundant work computing
   * the exact same four options from the exact same inputs. NO option is
   * `selected` (code-review M1) when the server's persisted tier id isn't
   * one of the four this FE table recognizes — the control must never
   * silently coerce that into a confident (and possibly wrong) tier/price;
   * see the trigger's fallback text below.
   */
  readonly options: readonly RegionTierOption[]
  /** Disables the trigger — used while a tier-change round trip
   * (`PUT /cart/lines/{sourceableId}/lead-time-tier`, UID-271) is pending, so
   * a buyer can't queue a second change before the first one resolves. */
  readonly disabled?: boolean
  readonly onSelect: (tierId: RegionTierId) => void
}

/**
 * UID-271 (Part B) — the "Ship as ready" custom shipment card's sourcing
 * region + lead-time control, rebuilt to the prototype's exact popdown shape
 * (`Fictiv Platform.dc.html` `shipCard.custBtnLabel`/`custOptions`, lines
 * ~2128-2148) in place of the old always-visible 4-tile radio grid
 * (`CustomRegionTierPicker`, superseded/removed by this ticket): a
 * "SOURCING REGION & LEAD TIME" label + a "Change" affordance, a single
 * trigger button showing the selected option's label/date/price, and a
 * popdown of every option (checkmark · label · date · price) opened on
 * click.
 *
 * Built on the shared `DropdownMenu` shell (`components/ui/DropdownMenu.tsx`)
 * rather than a bespoke listbox — it already implements everything WAI-ARIA's
 * menu-button pattern needs (trigger `aria-haspopup`/`aria-expanded`,
 * `role="menu"`/`role="menuitem"`, focus-into-panel on open, Up/Down roving
 * focus, `Escape` closes and returns focus to the trigger, click-outside
 * dismiss), so this control gets keyboard/AT support for free instead of a
 * fifth reimplementation of that shape in this codebase.
 */
export function CustomLeadTimeControl({
  options,
  disabled = false,
  onSelect,
}: CustomLeadTimeControlProps) {
  // Code-review M1: deliberately NO fallback to `options[0]` here — an
  // unrecognized persisted tier id (no option has `selected: true`) must
  // render as "unresolved", not silently default to Expedited/us with a
  // confident (and possibly wrong) price. `selected` stays `undefined` in
  // that case.
  const selected = options.find((option) => option.selected)

  return (
    <div className="custom-lead-time-control">
      <div className="custom-lead-time-control-head">
        <span className="custom-lead-time-control-label">Sourcing region &amp; lead time</span>
        {/* Decorative only in the prototype — the click target that opens
            the popdown is the trigger button below, not this text. */}
        <span className="custom-lead-time-control-change" aria-hidden="true">
          Change
        </span>
      </div>
      <DropdownMenu
        menuLabel="Sourcing region and lead time options"
        className="custom-lead-time-dropdown"
        renderTrigger={({ open, toggle, triggerRef }) => (
          <button
            ref={triggerRef}
            type="button"
            className="custom-lead-time-trigger"
            aria-haspopup="menu"
            aria-expanded={open}
            disabled={disabled}
            onClick={toggle}
          >
            <span className="custom-lead-time-trigger-text">
              <span className="custom-lead-time-trigger-label">
                {selected ? selected.label : 'Custom lead time'}
              </span>
              <span className="custom-lead-time-trigger-sub">
                {selected ? selected.arrivalLabel : 'Choose a sourcing region and lead time'}
              </span>
            </span>
            {/* UID-440 (PHASE 2) — the price is now a same-row flex SIBLING
             * of the label/date text stack, matching the RFQ row anatomy
             * (bold lead + muted sublabel on the left, a bold right-aligned
             * price on the right, rfq.css's `.quote-variant-price`) rather
             * than being folded into the muted sub line's own string. */}
            {selected && (
              <span className="custom-lead-time-trigger-price">{selected.priceLabel}</span>
            )}
            <DownOutlined aria-hidden="true" />
          </button>
        )}
        renderMenu={(close) => (
          <>
            {/* N2: this is semantically a single-select group, which
                `role="menuitemradio"`/`aria-checked` would name more
                precisely than `role="menuitem"`/`aria-current` — but the
                shared `DropdownMenu` shell's roving-focus/open-focus logic
                queries `[role="menuitem"]` specifically
                (`components/ui/DropdownMenu.tsx`), so switching only this
                option's role to `menuitemradio` would silently break
                Up/Down roving and the open-to-first-item focus move for
                this one menu. Kept `menuitem`/`aria-current` to compose
                cleanly with the primitive rather than fork it. */}
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                role="menuitem"
                className="dropdown-menu-item custom-lead-time-option"
                aria-current={option.selected}
                onClick={() => {
                  onSelect(option.id)
                  close()
                }}
              >
                <CheckOutlined
                  aria-hidden="true"
                  className={
                    option.selected
                      ? 'custom-lead-time-option-check'
                      : 'custom-lead-time-option-check custom-lead-time-option-check-hidden'
                  }
                />
                <span className="custom-lead-time-option-label">{option.label}</span>
                {/* UID-440 (PHASE 2) — split from one flat tertiary "date ·
                 * price" string into a muted date + a bold price, matching
                 * the RFQ row anatomy's bold-price emphasis (rfq.css's
                 * `.quote-variant-price`). */}
                <span className="custom-lead-time-option-meta">
                  <span className="custom-lead-time-option-date">{option.arrivalLabel}</span>
                  <span className="custom-lead-time-option-price">
                    {!option.selected && <span className="custom-lead-time-option-est">est.</span>}{' '}
                    {option.priceLabel}
                  </span>
                </span>
              </button>
            ))}
          </>
        )}
      />
    </div>
  )
}
